import { Calendar, Download, FileText, Loader2 } from 'lucide-react';
/**
 * Sub-spec 08 Plan 2 §2.3 — per-report main panel: filters + Generate +
 * chart + table + CSV download + Schedule this report.
 */
import { Suspense, lazy, useState } from 'react';
import { type DateRange, DateRangePicker, defaultDateRange } from './DateRangePicker';
import { type ReportColumn, type ReportRow, ReportTable } from './ReportTable';
import { ScheduleReportDrawer } from './ScheduleReportDrawer';
import { type ReportRegistryEntry, getReportEntry } from './registry';

const ReportChart = lazy(() => import('./charts/ReportChart'));

interface Props {
  slug: string;
  currency: string;
  timezone: string;
}

interface ReportResponse {
  reportType: string;
  columns: ReportColumn[];
  rows: ReportRow[];
  summary?: Record<string, unknown>;
  async?: boolean;
  rowCount?: number;
  generatedAt?: string;
  message?: string;
}

interface FetchError {
  status: number;
  error: string;
  message?: string | undefined;
}

function buildQuery(
  entry: ReportRegistryEntry,
  range: DateRange,
  filters: Record<string, string>,
): URLSearchParams {
  const q = new URLSearchParams();
  if (entry.requiresDateRange) {
    q.set('from', range.from);
    q.set('to', range.to);
  }
  for (const [k, v] of Object.entries(filters)) {
    if (v !== '' && v !== undefined && v !== null) q.set(k, v);
  }
  return q;
}

export function ReportPanel({ slug, currency, timezone }: Props) {
  const entry = getReportEntry(slug);
  const [range, setRange] = useState<DateRange>(defaultDateRange());
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ReportResponse | null>(null);
  const [err, setErr] = useState<FetchError | null>(null);
  const [csvToast, setCsvToast] = useState<string | null>(null);
  const [scheduleOpen, setScheduleOpen] = useState(false);

  if (!entry) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-surface p-8 text-center text-sm text-muted-foreground">
        Unknown report.
      </div>
    );
  }

  async function generate() {
    if (!entry) return;
    setLoading(true);
    setErr(null);
    setCsvToast(null);
    try {
      const q = buildQuery(entry, range, filters);
      const res = await fetch(`/api/reports/${entry.slug}?${q.toString()}`, {
        credentials: 'include',
      });
      const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
      if (!res.ok) {
        setErr({
          status: res.status,
          error: typeof body.error === 'string' ? body.error : `http_${res.status}`,
          message: typeof body.message === 'string' ? body.message : undefined,
        });
        setData(null);
        return;
      }
      setData(body as unknown as ReportResponse);
    } finally {
      setLoading(false);
    }
  }

  async function handleCsv() {
    if (!entry) return;
    setCsvToast(null);
    const q = buildQuery(entry, range, filters);
    // If the previous JSON came back async-flagged, we still hit /csv — the
    // route returns 202 with the async payload + fires the export.ready
    // trigger. Otherwise the browser downloads the inline file directly.
    if (data && data.async) {
      const res = await fetch(`/api/reports/${entry.slug}/csv?${q.toString()}`, {
        credentials: 'include',
      });
      if (res.status === 202) {
        setCsvToast('Export queued — you will receive an email when ready.');
      } else if (!res.ok) {
        setCsvToast('Export failed — try again or narrow the date range.');
      }
      return;
    }
    // Inline path — open in new tab so the browser handles
    // Content-Disposition: attachment.
    window.open(`/api/reports/${entry.slug}/csv?${q.toString()}`, '_blank');
  }

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-medium tracking-tight">{entry.title}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {entry.group === 'financial'
              ? 'Financial report — manager / owner only.'
              : 'Operational report.'}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setScheduleOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-3 py-1.5 text-sm press hover:bg-secondary"
        >
          <Calendar className="size-4" strokeWidth={1.75} />
          Schedule this report
        </button>
      </header>

      <section className="rounded-lg border border-border bg-surface p-4">
        <div className="grid gap-4 md:grid-cols-2">
          {entry.requiresDateRange ? (
            <DateRangePicker value={range} onChange={setRange} />
          ) : (
            <div className="text-sm text-muted-foreground">
              This report is point-in-time and ignores date range.
            </div>
          )}

          {entry.filters.length > 0 && (
            <div className="grid grid-cols-1 gap-3">
              {entry.filters.map((f) => (
                <div key={f.key}>
                  <label
                    htmlFor={`filter-${f.key}`}
                    className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
                  >
                    {f.label}
                  </label>
                  {f.kind === 'select' && f.options ? (
                    <select
                      id={`filter-${f.key}`}
                      value={filters[f.key] ?? ''}
                      onChange={(e) => setFilters((prev) => ({ ...prev, [f.key]: e.target.value }))}
                      className="h-10 w-full rounded-md border border-border bg-surface px-3 text-sm outline-none focus:ring-2 focus:ring-ring/30"
                    >
                      {f.options.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      id={`filter-${f.key}`}
                      type="text"
                      value={filters[f.key] ?? ''}
                      onChange={(e) => setFilters((prev) => ({ ...prev, [f.key]: e.target.value }))}
                      className="h-10 w-full rounded-md border border-border bg-surface px-3 text-sm outline-none focus:ring-2 focus:ring-ring/30"
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={generate}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground press disabled:opacity-50"
          >
            {loading ? (
              <Loader2 className="size-4 animate-spin" strokeWidth={1.75} />
            ) : (
              <FileText className="size-4" strokeWidth={1.75} />
            )}
            Generate
          </button>
          {data && (
            <button
              type="button"
              onClick={handleCsv}
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-3 py-1.5 text-sm press hover:bg-secondary"
            >
              <Download className="size-4" strokeWidth={1.75} />
              {data.async
                ? `Email me the CSV (${data.rowCount?.toLocaleString() ?? 'large'} rows)`
                : 'Download CSV'}
            </button>
          )}
        </div>

        {csvToast && (
          <div className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
            {csvToast}
          </div>
        )}
      </section>

      {err && (
        <div
          role="alert"
          className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900"
        >
          {err.status === 422 && (
            <p>This date range is too wide — narrow it or use the CSV export.</p>
          )}
          {err.status === 403 && <p>You don't have access to this report.</p>}
          {err.status === 402 && <p>Tenant is past_due — billing must be resolved.</p>}
          {err.status === 400 && <p>{err.message ?? 'Bad request.'}</p>}
          {err.status !== 422 && err.status !== 403 && err.status !== 402 && err.status !== 400 && (
            <p>{err.message ?? `Request failed (${err.status}).`}</p>
          )}
        </div>
      )}

      {data && !data.async && (
        <>
          {entry.chart !== 'none' && (
            <Suspense
              fallback={
                <div className="h-[280px] w-full animate-pulse rounded-lg border border-border bg-surface" />
              }
            >
              <ReportChart
                chart={entry.chart}
                columns={data.columns}
                rows={data.rows}
                currency={currency}
              />
            </Suspense>
          )}
          <ReportTable
            columns={data.columns}
            rows={data.rows}
            currency={currency}
            timezone={timezone}
          />
        </>
      )}

      {data?.async && (
        <div className="rounded-lg border border-dashed border-border bg-surface p-6 text-center text-sm text-muted-foreground">
          <p>
            This report has <span className="num">{data.rowCount?.toLocaleString()}</span> rows —
            too many for inline display.
          </p>
          <p className="mt-1">
            Click "Email me the CSV" above to receive a download link by email.
          </p>
        </div>
      )}

      {scheduleOpen && (
        <ScheduleReportDrawer
          reportType={entry.slug}
          reportTitle={entry.title}
          paramsJson={JSON.stringify({
            ...(entry.requiresDateRange ? { from: range.from, to: range.to } : {}),
            ...filters,
          })}
          onClose={() => setScheduleOpen(false)}
        />
      )}
    </div>
  );
}
